(() => {
  const selectors = {
    buyButtons: '[data-buy-buttons], .book-buy-buttons',
    form: '.book-buy-buttons__form',
    variantsJson: '[data-product-variants-json]',
    variantSelect: '[data-variant-select]',
    variantPills: '.product-variant-pills',
    variantRadio: '.product-variant-pills input[type="radio"]',
    hiddenVariantInput: 'input[type="hidden"][name="id"], [data-selected-variant-id]',
    quantityInput: '.book-buy-buttons__quantity .quantity__input',
    quantityWrapper: '.book-buy-buttons__quantity .quantity',
    quantityButton: '.book-buy-buttons__quantity .quantity__button',
    giftCardRecipient: '[data-gift-card-recipient]',
    giftCardRecipientToggle: '[data-gift-card-recipient-toggle]',
    giftCardRecipientFields: '[data-gift-card-recipient-fields]',
    giftCardRecipientRequired: '[data-gift-card-recipient-required]',
    giftCardRecipientOffset: '[data-gift-card-recipient-timezone-offset]',
    price: '[data-product-price]',
    currentPrice: '[data-product-current-price]',
    compareAtPrice: '[data-product-compare-at-price]',
    saleLabel: '[data-product-sale-label]',
    soldOutLabel: '[data-product-sold-out-label]',
    unitPrice: '[data-product-unit-price]',
    condition: '[data-product-condition]',
    stockNote: '[data-stock-note]',
    pickupMessage: '[data-pickup-message]',
    selectOptionsButton: '[data-select-options-button]',
    addToCartButton: '[data-add-to-cart-button]',
    dynamicCheckoutButtons: '[data-dynamic-checkout-buttons]',
    paymentTermsVariantInput: '[data-payment-terms-variant-id]',
    paymentTerms: '[data-payment-terms]',
    sellingPlanOptions: '[data-selling-plan-options]',
    sellingPlanOption: '[data-selling-plan-option]',
    sellingPlanInput: '[data-selling-plan-input]',
    sellingPlanPrice: '[data-selling-plan-price]',
    bookshopButton: '[data-bookshop-button]',
    soldOutButton: '[data-sold-out-button]',
    bookshopFormatButton: '[data-bookshop-format-button]',
    freeShippingProgress: '[data-free-shipping-progress][data-free-shipping-context="product"]'
  };

  function handleize(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function getRoot(element) {
    return element?.closest?.(selectors.buyButtons) || null;
  }

  function getForm(root) {
    return root.querySelector(selectors.form) || root.querySelector('form');
  }

  function parseVariants(root) {
    const script = root.querySelector(selectors.variantsJson);

    if (!script) return [];

    try {
      const variants = JSON.parse(script.textContent);
      return Array.isArray(variants) ? variants : [];
    } catch (error) {
      console.error('Could not parse product variant data.', error);
      return [];
    }
  }

  function getSelectedOptions(root) {
    const fieldsets = Array.from(root.querySelectorAll('.product-variant-pills__fieldset'));

    if (!fieldsets.length) return [];

    return fieldsets.map((fieldset) => {
      const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
      return checkedInput ? checkedInput.value : null;
    });
  }

  function getSelectedVariant(root, variants = parseVariants(root)) {
    if (!variants.length) return null;

    const select = root.querySelector(selectors.variantSelect);

    if (select) {
      const selectedId = Number(select.value);
      return variants.find((variant) => Number(variant.id) === selectedId) || null;
    }

    const selectedOptions = getSelectedOptions(root);

    if (selectedOptions.length) {
      return (
        variants.find((variant) => {
          if (!Array.isArray(variant.options)) return false;

          return variant.options.every((optionValue, index) => {
            return optionValue === selectedOptions[index];
          });
        }) || null
      );
    }

    const hiddenInput = root.querySelector(selectors.hiddenVariantInput);

    if (hiddenInput) {
      const selectedId = Number(hiddenInput.value);
      return variants.find((variant) => Number(variant.id) === selectedId) || variants[0] || null;
    }

    return variants[0] || null;
  }

  function getAvailableVariantCount(variants) {
    return variants.filter((variant) => variant.available).length;
  }

  function isCompactContext(root) {
    const context = root.dataset.buttonContext || '';

    return ['card', 'compact', 'product-card'].includes(context);
  }

  function getQuantityLimits(input) {
    const min = parseInt(input.getAttribute('min'), 10) || 0;
    const step = parseInt(input.getAttribute('step'), 10) || 1;

    const maxAttribute = input.getAttribute('max');
    const max =
      maxAttribute !== null && maxAttribute !== ''
        ? parseInt(maxAttribute, 10)
        : null;

    return { min, max, step };
  }

  function clampQuantity(value, min, max) {
    if (value < min) return min;
    if (max !== null && value > max) return max;
    return value;
  }

  function getQuantity(root) {
    const input = root.querySelector(selectors.quantityInput);

    if (!input) return 1;

    const quantity = parseInt(input.value, 10);

    if (Number.isNaN(quantity)) return 1;

    return Math.max(quantity, 0);
  }

  function formatMoney(cents, moneyFormat, currency) {
    const numericCents = Number(cents) || 0;

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(numericCents, moneyFormat);
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(numericCents / 100);
  }

  function getUnitPriceLabel(variant) {
  if (!variant || variant.has_unit_price !== true || !variant.unit_price) {
    return '';
  }

  const referenceUnit = variant.unit_price_reference_unit || '';
  const referenceValue = Number(variant.unit_price_reference_value);
  const referenceValueLabel =
    Number.isFinite(referenceValue) && referenceValue !== 1 ? String(referenceValue) : '';

  if (!referenceUnit) {
    return '';
  }

  return `${variant.unit_price} / ${referenceValueLabel}${referenceUnit}`;
}

  function updateButtonState(button, shouldShow, shouldDisable = false) {
    if (!button) return;

    button.hidden = !shouldShow;

    if ('disabled' in button) {
      button.disabled = shouldDisable;
    }

    if (shouldDisable) {
      button.setAttribute('aria-disabled', 'true');
    } else {
      button.removeAttribute('aria-disabled');
    }
  }

  function updatePrice(root, variant) {
    const priceWrapper = root.querySelector(selectors.price);

    if (!priceWrapper || !variant) return;

    const hasVariantSelectors = Boolean(
      root.querySelector(selectors.variantSelect) || root.querySelector(selectors.variantRadio)
    );

    if (priceWrapper.dataset.staticCardPrice === 'true' && !hasVariantSelectors) return;
    const currentPrice = priceWrapper.querySelector(selectors.currentPrice);
    const compareAtPrice = priceWrapper.querySelector(selectors.compareAtPrice);
    const saleLabel = priceWrapper.querySelector(selectors.saleLabel);
    const soldOutLabel = priceWrapper.querySelector(selectors.soldOutLabel);
    const unitPrice = priceWrapper.querySelector(selectors.unitPrice);

    const priceCents = Number(variant.price_cents) || 0;
    const compareAtPriceCents = Number(variant.compare_at_price_cents) || 0;
    const isOnSale = compareAtPriceCents > priceCents;
    const isSoldOut = variant.available === false;
    const unitPriceLabel = getUnitPriceLabel(variant);

    priceWrapper.classList.toggle('is-on-sale', isOnSale);
    priceWrapper.classList.toggle('is-sold-out', isSoldOut);

    if (currentPrice) {
      currentPrice.textContent = variant.price || formatMoney(priceCents);
      currentPrice.classList.add('price--current');
      currentPrice.classList.toggle('price--strikethrough', isSoldOut);
    }

    if (compareAtPrice) {
      compareAtPrice.textContent = variant.compare_at_price || '';
      compareAtPrice.hidden = !isOnSale;
    }

    if (saleLabel) {
      saleLabel.hidden = !isOnSale;
    }

    if (soldOutLabel) {
      soldOutLabel.hidden = !isSoldOut;
    }

    if (unitPrice) {
      unitPrice.textContent = unitPriceLabel;
      unitPrice.hidden = unitPriceLabel === '';
    }
  }

  function updateCondition(root, variant) {
    const condition = root.querySelector(selectors.condition);

    if (!condition || !variant) return;

    condition.textContent = variant.condition_label || '';
    condition.hidden = !variant.condition_label;
  }

  function updateHiddenVariantInput(root, variant) {
    const form = getForm(root);
    const hiddenInput = form?.querySelector(selectors.hiddenVariantInput);

    if (!hiddenInput || !variant) return;

    hiddenInput.value = String(variant.id);
  }

  function updatePaymentTermsVariant(root, variant) {
    if (!root || !variant) return;

    const paymentTermsVariantInput = root.querySelector(selectors.paymentTermsVariantInput);
    const paymentTermsWrapper = root.querySelector(selectors.paymentTerms);

    if (paymentTermsVariantInput) {
      paymentTermsVariantInput.value = String(variant.id);
    }

    if (paymentTermsWrapper) {
      paymentTermsWrapper
        .querySelectorAll('shopify-payment-terms')
        .forEach((paymentTermsElement) => {
          paymentTermsElement.setAttribute('variant-id', String(variant.id));
        });
    }
  }

  function getSellingPlanAllocations(variant) {
    if (!variant || !Array.isArray(variant.selling_plan_allocations)) {
      return [];
    }

    return variant.selling_plan_allocations;
  }

  function variantAllowsSellingPlan(variant, sellingPlanId) {
    if (!sellingPlanId) return true;

    const planIds = Array.isArray(variant?.selling_plan_ids) ? variant.selling_plan_ids : [];

    return planIds.some((planId) => String(planId) === String(sellingPlanId));
  }

  function updateSellingPlanOptions(root, variant) {
    const wrapper = root.querySelector(selectors.sellingPlanOptions);

    if (!wrapper || !variant) return;

    const options = Array.from(wrapper.querySelectorAll(selectors.sellingPlanOption));
    const hiddenInput = wrapper.querySelector(selectors.sellingPlanInput);
    const requiresSellingPlan = wrapper.dataset.requiresSellingPlan === 'true';
    const allocations = getSellingPlanAllocations(variant);
    const allocationById = new Map(
      allocations.map((allocation) => [String(allocation.id), allocation])
    );

    let checkedOption = null;
    let firstAvailablePlanOption = null;

    options.forEach((option) => {
      const planId = option.dataset.sellingPlanId || '';
      const isOneTime = planId === '';
      const isAvailable = isOneTime
        ? !requiresSellingPlan
        : variantAllowsSellingPlan(variant, planId);

      option.disabled = !isAvailable;
      option.closest('.book-buy-buttons__purchase-option')?.classList.toggle('is-disabled', !isAvailable);

      if (isAvailable && !isOneTime && !firstAvailablePlanOption) {
        firstAvailablePlanOption = option;
      }

      if (option.checked) {
        checkedOption = option;
      }
    });

    if (!checkedOption || checkedOption.disabled) {
      checkedOption = requiresSellingPlan ? firstAvailablePlanOption : options.find((option) => option.dataset.sellingPlanId === '');

      if (!checkedOption || checkedOption.disabled) {
        checkedOption = firstAvailablePlanOption || null;
      }

      if (checkedOption) {
        checkedOption.checked = true;
      }
    }

    if (hiddenInput) {
      hiddenInput.value = checkedOption?.dataset.sellingPlanId || '';
      hiddenInput.disabled = hiddenInput.value === '';
    }

    wrapper.querySelectorAll(selectors.sellingPlanPrice).forEach((priceElement) => {
      const planId = priceElement.dataset.sellingPlanPriceId || '';
      const allocation = allocationById.get(String(planId));

      if (allocation?.price) {
        priceElement.textContent = allocation.price;
        priceElement.hidden = false;
      } else {
        priceElement.textContent = '';
        priceElement.hidden = true;
      }
    });
  }

  function updateQuantityButtons(wrapper, value, min, max) {
    if (!wrapper) return;

    const minusButton = wrapper.querySelector('[data-quantity-action="minus"]');
    const plusButton = wrapper.querySelector('[data-quantity-action="plus"]');
    const isZeroedOut = max === 0;

    if (minusButton) {
      const isDisabled = isZeroedOut || value <= min;
      minusButton.disabled = isDisabled;
      minusButton.classList.toggle('is-disabled', isDisabled);
    }

    if (plusButton) {
      const isDisabled = isZeroedOut || (max !== null && value >= max);
      plusButton.disabled = isDisabled;
      plusButton.classList.toggle('is-disabled', isDisabled);
    }
  }

  function updateQuantity(root, variant, options = {}) {
    const input = root.querySelector(selectors.quantityInput);
    const wrapper = root.querySelector(selectors.quantityWrapper);

    if (!input || !variant) return;

    const isAvailable = variant.available !== false;
    const variantMax = Number(variant.quantity_max);
    const max = isAvailable ? (Number.isFinite(variantMax) ? variantMax : 100) : 0;
    const min = isAvailable ? 1 : 0;

    input.min = String(min);
    input.max = String(max);

    let currentValue = parseInt(input.value, 10);

    if (Number.isNaN(currentValue)) {
      currentValue = min;
    }

    if (options.resetToMinimum) {
      currentValue = min;
    }

    currentValue = clampQuantity(currentValue, min, max);
    input.value = String(currentValue);

    updateQuantityButtons(wrapper, currentValue, min, max);
  }

    function updateStockNote(root, variant) {
    const stockNote = root.querySelector(selectors.stockNote);

    if (!stockNote || !variant) return;

    const inventoryQuantity = Number(variant.inventory_quantity) || 0;
    const tracksInventory = variant.inventory_management === 'shopify';
    const deniesOverselling = variant.inventory_policy === 'deny';

    if (!variant.available || !tracksInventory || !deniesOverselling || inventoryQuantity <= 0) {
      stockNote.textContent = '';
      return;
    }

    if (inventoryQuantity === 1) {
      stockNote.textContent = stockNote.dataset.lowStockOne || 'Only 1 left';
    } else if (inventoryQuantity === 2) {
      stockNote.textContent = stockNote.dataset.lowStockTwo || 'Only 2 left';
    } else {
      stockNote.textContent = '';
    }
  }

  function updatePickupMessage(root, variant) {
    const pickupMessage = root.querySelector(selectors.pickupMessage);

    if (!pickupMessage || !variant) return;

    const hasPickupMessage =
      variant.available !== false &&
      variant.pickup_available === true &&
      typeof variant.pickup_message === 'string' &&
      variant.pickup_message.trim() !== '';

    pickupMessage.hidden = !hasPickupMessage;

    if (hasPickupMessage) {
      pickupMessage.textContent = variant.pickup_message;
    } else {
      pickupMessage.textContent = '';
    }
  }

  function updatePrimaryButtons(root, variant, variants) {
    const selectOptionsButton = root.querySelector(selectors.selectOptionsButton);
    const dynamicCheckoutButtons = root.querySelector(selectors.dynamicCheckoutButtons);
    const addToCartButton = root.querySelector(selectors.addToCartButton);
    const bookshopButton = root.querySelector(selectors.bookshopButton);
    const soldOutButton = root.querySelector(selectors.soldOutButton);

    const compact = isCompactContext(root);
    const availableVariantCount = getAvailableVariantCount(variants);

    const showSelectOptions =
      compact && variants.length > 1 && availableVariantCount > 1;

    const showAddToCart =
      !showSelectOptions && Boolean(variant?.available);

    const showBookshop =
      !showSelectOptions &&
      variant?.available === false &&
      Boolean(variant?.bookshop_oos_eligible) &&
      Boolean(variant?.bookshop_url);

    const showSoldOut =
      !showSelectOptions &&
      variant?.available === false &&
      !showBookshop;

    updateButtonState(selectOptionsButton, showSelectOptions, false);
    updateButtonState(addToCartButton, showAddToCart, !showAddToCart);

    if (dynamicCheckoutButtons) {
      dynamicCheckoutButtons.hidden = !showAddToCart;
    }

    if (bookshopButton) {
      if (variant?.bookshop_url) {
        bookshopButton.href = variant.bookshop_url;
      }

      updateButtonState(bookshopButton, showBookshop, false);
    }

    updateButtonState(soldOutButton, showSoldOut, true);
  }

  function updateAlternateFormatButtons(root, variant) {
    const bookshopFormatButton = root.querySelector(selectors.bookshopFormatButton);

    if (!bookshopFormatButton || !variant) return;

    if (variant.bookshop_url) {
      bookshopFormatButton.href = variant.bookshop_url;
      bookshopFormatButton.hidden = false;
    } else {
      bookshopFormatButton.hidden = true;
    }
  }

  function updateFreeShippingProgress(root, variant) {
    const progressBlocks = root.querySelectorAll(selectors.freeShippingProgress);

    if (!progressBlocks.length || !variant) return;

    progressBlocks.forEach((progress) => {
      const cartTotalCents = Number(progress.dataset.freeShippingCartTotal) || 0;
      const thresholdCents = Number(progress.dataset.freeShippingThreshold) || 0;
      const variantPriceCents = Number(variant.price_cents) || 0;
      const quantity = getQuantity(root);

      if (variant.available === false) {
        progress.hidden = true;
        return;
      }

      const projectedTotalCents = cartTotalCents + variantPriceCents * quantity;
      const remainingCents = Math.max(thresholdCents - projectedTotalCents, 0);
      const percent =
        thresholdCents > 0
          ? Math.min(Math.round((projectedTotalCents / thresholdCents) * 100), 100)
          : 0;

      const cartQualifies = thresholdCents > 0 && cartTotalCents >= thresholdCents;
      const projectedQualifies = thresholdCents > 0 && projectedTotalCents >= thresholdCents;

      const message = progress.querySelector('[data-free-shipping-message]');
      const bar = progress.querySelector('[data-free-shipping-bar]');
      const progressbar = progress.querySelector('[data-free-shipping-progressbar]');

      const moneyFormat = progress.dataset.freeShippingMoneyFormat;
      const currency = progress.dataset.freeShippingCurrency;

      const remainingMessage =
        progress.dataset.freeShippingRemainingMessage ||
        'Add this + [amount] more for free shipping.';

      const qualifiedMessage =
        progress.dataset.freeShippingQualifiedMessage ||
        'Add this to unlock free shipping.';

      const cartQualifiedMessage =
        progress.dataset.freeShippingCartQualifiedMessage ||
        "You've unlocked free shipping!";

      let nextMessage = remainingMessage.replace(
        '[amount]',
        formatMoney(remainingCents, moneyFormat, currency)
      );

      if (cartQualifies) {
        nextMessage = cartQualifiedMessage;
      } else if (projectedQualifies) {
        nextMessage = qualifiedMessage;
      }

      progress.hidden = false;
      progress.dataset.freeShippingVariantPrice = String(variantPriceCents);

      if (message) {
        message.textContent = nextMessage;
      }

      if (bar) {
        bar.style.width = `${percent}%`;
      }

      if (progressbar) {
        progressbar.setAttribute('aria-valuenow', String(percent));
      }
    });
  }

  function dispatchVariantChange(root, variant) {
    if (!variant) return;

    const productRoot =
      root.closest('.product-showcase') ||
      root.closest('.product') ||
      root.closest('.product-card') ||
      root;

    if (variant.featured_image_id) {
      const galleries = productRoot.querySelectorAll('[data-product-gallery]');

      galleries.forEach((gallery) => {
        gallery.dispatchEvent(
          new CustomEvent('product-gallery:select-image', {
            bubbles: true,
            detail: {
              imageId: String(variant.featured_image_id),
              variant,
              productId: root.dataset.productId || null
            }
          })
        );
      });
    }

    document.dispatchEvent(
      new CustomEvent('product:variant-change', {
        detail: {
          root,
          productId: root.dataset.productId || null,
          variant
        }
      })
    );
  }

  function updateBuyButtons(root, options = {}) {
    const variants = parseVariants(root);
    const variant = getSelectedVariant(root, variants);

    if (!variant) return;

    updateHiddenVariantInput(root, variant);
    updatePaymentTermsVariant(root, variant);
    updateSellingPlanOptions(root, variant);
    updatePrice(root, variant);
    updateCondition(root, variant);
    updateQuantity(root, variant, options);
    updateStockNote(root, variant);
    updatePickupMessage(root, variant);
    updatePrimaryButtons(root, variant, variants);
    updateAlternateFormatButtons(root, variant);
    updateFreeShippingProgress(root, variant);
    dispatchVariantChange(root, variant);
  }

  function updateVariantVisibilitySections(productId, variant) {
    if (!productId || !variant) return;

    const productIdSelector = CSS.escape(String(productId));
    const sections = document.querySelectorAll(
      `[data-custom-layout-variant-visibility][data-custom-layout-product-id="${productIdSelector}"]`
    );

    sections.forEach((section) => {
      const requiredValue = section.dataset.customLayoutConditionValue || '';
      const requiredHandle = section.dataset.customLayoutConditionHandle || '';
      const variantCondition = variant.condition_label || '';
      const variantConditionValue = variantCondition.toLowerCase().trim();
      const variantConditionHandle = handleize(variantCondition);
      const shouldShow =
        variantConditionValue === requiredValue || variantConditionHandle === requiredHandle;

      section.hidden = !shouldShow;
    });
  }

  function handleQuantityButtonClick(event) {
    const button = event.target.closest('[data-quantity-action]');
    if (!button || button.disabled) return;

    const root = getRoot(button);
    if (!root) return;

    const wrapper = button.closest('.quantity');
    const input = wrapper?.querySelector('.quantity__input');
    if (!input) return;

    const { min, max, step } = getQuantityLimits(input);

    if (max === 0) return;

    const currentValue = parseInt(input.value, 10) || min;
    const action = button.dataset.quantityAction;

    let newValue = currentValue;

    if (action === 'plus') {
      newValue += step;
    } else if (action === 'minus') {
      newValue -= step;
    }

    newValue = clampQuantity(newValue, min, max);
    input.value = String(newValue);

    updateQuantityButtons(wrapper, newValue, min, max);
    updateBuyButtons(root);
  }

  function handleInputChange(event) {
    const target = event.target;
    const root = getRoot(target);

    if (!root) return;

    if (target.matches(selectors.variantRadio) || target.matches(selectors.variantSelect)) {
      updateBuyButtons(root, { resetToMinimum: true });
      return;
    }

    if (target.matches(selectors.sellingPlanOption)) {
      updateBuyButtons(root);
      return;
    }

    if (target.matches(selectors.quantityInput)) {
      const input = target;
      const wrapper = input.closest('.quantity');
      const { min, max } = getQuantityLimits(input);
      const currentValue = parseInt(input.value, 10);

      if (Number.isNaN(currentValue)) return;

      const newValue = clampQuantity(currentValue, min, max);
      input.value = String(newValue);

      updateQuantityButtons(wrapper, newValue, min, max);
      updateBuyButtons(root);
    }
  }

  function updateGiftCardRecipientForm(recipientForm) {
  if (!recipientForm) return;

  const toggle = recipientForm.querySelector(selectors.giftCardRecipientToggle);
  const fields = recipientForm.querySelector(selectors.giftCardRecipientFields);
  const requiredFields = recipientForm.querySelectorAll(selectors.giftCardRecipientRequired);
  const offsetInput = recipientForm.querySelector(selectors.giftCardRecipientOffset);

  if (!toggle || !fields) return;

  const isEnabled = toggle.checked;

  fields.hidden = !isEnabled;

  fields.querySelectorAll('input, textarea, select').forEach((field) => {
    if (field === offsetInput) return;
    field.disabled = !isEnabled;
  });

  requiredFields.forEach((field) => {
    field.required = isEnabled;
  });

  if (offsetInput) {
    offsetInput.disabled = !isEnabled;
    offsetInput.value = isEnabled ? String(new Date().getTimezoneOffset()) : '';
  }
}

  function initGiftCardRecipientForms(container = document) {
    container.querySelectorAll(selectors.giftCardRecipient).forEach((recipientForm) => {
      if (recipientForm.dataset.giftCardRecipientInitialized === 'true') return;

      recipientForm.dataset.giftCardRecipientInitialized = 'true';

      recipientForm.addEventListener('change', (event) => {
        if (event.target.matches(selectors.giftCardRecipientToggle)) {
          updateGiftCardRecipientForm(recipientForm);
        }
      });

      updateGiftCardRecipientForm(recipientForm);
    });
  }
  

  function initBuyButtons(container = document) {
    container.querySelectorAll(selectors.buyButtons).forEach((root) => {
      if (root.dataset.buyButtonsInitialized === 'true') return;

      root.dataset.buyButtonsInitialized = 'true';

      root.addEventListener('click', handleQuantityButtonClick);
      root.addEventListener('change', handleInputChange);
      root.addEventListener('input', handleInputChange);

      updateBuyButtons(root, { resetToMinimum: false });
    });

        initGiftCardRecipientForms(container);
  }

  document.addEventListener('DOMContentLoaded', () => {
    initBuyButtons();
  });

  document.addEventListener('product:variant-change', (event) => {
    updateVariantVisibilitySections(event.detail?.productId, event.detail?.variant);
  });

  document.addEventListener('shopify:section:load', (event) => {
    initBuyButtons(event.target);
  });

  window.GLBBuyButtons = {
    init: initBuyButtons,
    update(root) {
      if (!root) return;

      const buyButtonsRoot = root.matches?.(selectors.buyButtons) ? root : root.querySelector?.(selectors.buyButtons);

      if (buyButtonsRoot) {
        updateBuyButtons(buyButtonsRoot);
      }
    }
  };
})();
